import React, { useCallback, useId, useRef, useState } from 'react';
import styled from 'styled-components';

import { AssetPickerBrowseButton } from './asset-picker-control';

type ImageSourceTab = 'library' | 'url';

const SOURCE_TABS: ReadonlyArray<{ id: ImageSourceTab; label: string }> = [
  { id: 'library', label: 'Library' },
  { id: 'url', label: 'URL' },
];

const Stack = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const TabList = styled.div`
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const TabButton = styled.button`
  background: var(--dcm-cms-image-source-action-bg, var(--puck-color-white, #fff));
  border: 1px solid var(--dcm-cms-image-source-action-border, var(--puck-color-border, #d0d4d8));
  border-radius: var(--dcm-cms-image-source-action-radius, 4px);
  color: var(--dcm-cms-image-source-action-color, var(--puck-color-text, #181b1f));
  cursor: pointer;
  font-size: 12px;
  min-width: 0;
  padding: 7px 4px;
  text-align: center;

  &[aria-selected='true'] {
    box-shadow: inset 0 -2px 0 var(--dcm-cms-image-source-selected, var(--puck-color-azure-05, #0b99e6));
    font-weight: 600;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

const UrlInput = styled.input`
  border: 1px solid var(--dcm-cms-image-source-action-border, var(--puck-color-border, #d0d4d8));
  border-radius: var(--dcm-cms-image-source-action-radius, 4px);
  box-sizing: border-box;
  font-size: 14px;
  padding: 7px 8px;
  width: 100%;

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

type LibraryUrlTabsProps = {
  enterpriseId: string | undefined;
  fieldLabel: string;
  name: string;
  value: string;
  onBrowseLibrary: () => void;
  onUrlChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  browseTestId: string;
  inputTestId: string;
};

/** Shared Library-first image chooser for string-URL fields. */
export function LibraryUrlTabs({
  enterpriseId,
  fieldLabel,
  name,
  value,
  onBrowseLibrary,
  onUrlChange,
  readOnly,
  browseTestId,
  inputTestId,
}: LibraryUrlTabsProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<ImageSourceTab>(() => (value ? 'url' : 'library'));
  const reactId = useId();
  const idBase = `library-url-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const libraryTabRef = useRef<HTMLButtonElement>(null);
  const urlTabRef = useRef<HTMLButtonElement>(null);

  const tabRefFor = useCallback(
    (tab: ImageSourceTab): React.RefObject<HTMLButtonElement> => (tab === 'url' ? urlTabRef : libraryTabRef),
    []
  );

  const activateTab = useCallback(
    (tab: ImageSourceTab, moveFocus = false) => {
      setActiveTab(tab);
      if (moveFocus) requestAnimationFrame(() => tabRefFor(tab).current?.focus());
    },
    [tabRefFor]
  );

  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, tab: ImageSourceTab) => {
      const currentIndex = SOURCE_TABS.findIndex(({ id }) => id === tab);
      let nextIndex: number | undefined;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % SOURCE_TABS.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + SOURCE_TABS.length) % SOURCE_TABS.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = SOURCE_TABS.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      activateTab(SOURCE_TABS[nextIndex].id, true);
    },
    [activateTab]
  );

  const tabIdFor = (tab: ImageSourceTab): string => `${idBase}-${tab}-tab`;
  const panelIdFor = (tab: ImageSourceTab): string => `${idBase}-${tab}-panel`;

  return (
    <Stack>
      <TabList role='tablist' aria-label={`${fieldLabel} source`}>
        {SOURCE_TABS.map(({ id, label }) => (
          <TabButton
            key={id}
            ref={tabRefFor(id)}
            id={tabIdFor(id)}
            type='button'
            role='tab'
            aria-controls={panelIdFor(id)}
            aria-label={label}
            aria-selected={activeTab === id}
            tabIndex={activeTab === id ? 0 : -1}
            disabled={readOnly}
            onClick={() => activateTab(id)}
            onKeyDown={(event) => handleTabKeyDown(event, id)}
          >
            {label}
          </TabButton>
        ))}
      </TabList>

      <div
        id={panelIdFor('library')}
        role='tabpanel'
        aria-labelledby={tabIdFor('library')}
        hidden={activeTab !== 'library'}
      >
        <AssetPickerBrowseButton
          enterpriseId={enterpriseId}
          onClick={onBrowseLibrary}
          disabled={readOnly}
          testId={browseTestId}
          style={{ width: '100%' }}
        />
      </div>

      <div id={panelIdFor('url')} role='tabpanel' aria-labelledby={tabIdFor('url')} hidden={activeTab !== 'url'}>
        <UrlInput
          type='text'
          value={value}
          onChange={onUrlChange}
          disabled={readOnly}
          placeholder='https://...'
          aria-label={`${fieldLabel} URL`}
          data-testid={inputTestId}
        />
      </div>
    </Stack>
  );
}
